import { CONCIERGE_RETURN_KEY, conciergeRequestFromSearch, conciergeRequestPath } from '../lib/concierge-link';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../lib/ConfigContext';

interface AuthState {
  idToken: string | null;
  accessToken: string | null;
  email: string | null;
  expiresAt: number | null;
}

interface AuthContextValue extends AuthState {
  signIn: () => void;
  signOut: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const STORAGE_KEY = 'manor.admin.auth';
const EMPTY_STATE: AuthState = { idToken: null, accessToken: null, email: null, expiresAt: null };

function decodeJwt(token: string): { email?: string; exp?: number } {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

// sessionStorage throws in Safari private-mode, iOS Lockdown Mode, and any
// storage-blocked context. loadStored runs at mount inside useState's
// initializer, so an uncaught throw crashes the whole portal render before any
// error boundary is on the tree. Every access is guarded and falls back to
// in-memory-only auth state — the user can still sign in for the session,
// they just won't survive a refresh.
function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage blocked — auth lives in memory for this tab */
  }
}

function safeSessionRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* storage blocked — nothing to do */
  }
}

function loadStored(): AuthState {
  const raw = safeSessionGet(STORAGE_KEY);
  if (!raw) return EMPTY_STATE;
  try {
    const parsed = JSON.parse(raw) as AuthState;
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
      safeSessionRemove(STORAGE_KEY);
      return EMPTY_STATE;
    }
    return parsed;
  } catch {
    return EMPTY_STATE;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const config = useConfig();
  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>(loadStored);
  const [loading, setLoading] = useState(false);
  const exchangedCodes = useRef<Set<string>>(new Set());

  const persist = useCallback((next: AuthState) => {
    setState(next);
    if (next.idToken) safeSessionSet(STORAGE_KEY, JSON.stringify(next));
    else safeSessionRemove(STORAGE_KEY);
  }, []);

  const signIn = useCallback(() => {
    const request = conciergeRequestFromSearch(window.location.search);
    if (request) safeSessionSet(CONCIERGE_RETURN_KEY, request);
    else safeSessionRemove(CONCIERGE_RETURN_KEY);
    const params = new URLSearchParams({
      client_id: config.cognitoClientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: `${config.portalUrl}/auth/callback`,
      ...(config.identityProvider === 'COGNITO' ? {} : { identity_provider: 'SignInWithApple' }),
    });
    window.location.href = `https://${config.cognitoDomain}/oauth2/authorize?${params}`;
  }, [config]);

  const signOut = useCallback(() => {
    persist({ idToken: null, accessToken: null, email: null, expiresAt: null });
    const params = new URLSearchParams({
      client_id: config.cognitoClientId,
      logout_uri: config.portalUrl,
    });
    window.location.href = `https://${config.cognitoDomain}/logout?${params}`;
  }, [config, persist]);

  // Handle the OAuth callback: exchange code for tokens.
  // Authorization codes are single-use; React StrictMode + react-router's
  // unstable navigate ref can re-fire this effect, so we guard with a ref.
  useEffect(() => {
    if (window.location.pathname !== '/auth/callback') return;
    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) return;
    if (exchangedCodes.current.has(code)) return;
    exchangedCodes.current.add(code);
    setLoading(true);
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.cognitoClientId,
      code,
      redirect_uri: `${config.portalUrl}/auth/callback`,
    });
    fetch(`https://${config.cognitoDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
      .then(async (r) => {
        if (!r.ok) {
          const text = await r.text().catch(() => '');
          let detail = `HTTP ${r.status}`;
          try {
            const j = JSON.parse(text);
            if (j.error) detail = `${j.error}${j.error_description ? `: ${j.error_description}` : ''}`;
          } catch {
            if (text) detail = `HTTP ${r.status}: ${text.slice(0, 200)}`;
          }
          throw new Error(`Token exchange ${detail}`);
        }
        return r.json();
      })
      .then((tokens) => {
        const decoded = decodeJwt(tokens.id_token);
        persist({
          idToken: tokens.id_token,
          accessToken: tokens.access_token,
          email: decoded.email ?? null,
          expiresAt: decoded.exp ? decoded.exp * 1000 : Date.now() + 8 * 3600 * 1000,
        });
        const request = safeSessionGet(CONCIERGE_RETURN_KEY);
        safeSessionRemove(CONCIERGE_RETURN_KEY);
        navigate(request ? conciergeRequestPath(request) : '/', { replace: true });
      })
      .catch((e) => {
        console.error('Token exchange failed', e);
        navigate('/login?error=' + encodeURIComponent(String(e?.message ?? e)), { replace: true });
      })
      .finally(() => setLoading(false));
  }, [config, navigate, persist]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn,
      signOut,
      isAuthenticated: !!state.idToken,
      loading,
    }),
    [state, signIn, signOut, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
